import { useEffect, useRef } from "react";

declare global {
  interface Window {
    p5: any;
    __seaSketch?: any;
  }
}

export default function Home() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load p5.js from official CDN
    const ensureP5 = (): Promise<void> =>
      new Promise((resolve) => {
        if (window.p5) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/p5.js/1.9.4/p5.min.js";
        script.onload = () => resolve();
        document.head.appendChild(script);
      });

    let p5Instance: any = null;

    ensureP5().then(() => {
      if (!containerRef.current) return;

      // p5.js sketch: 16:9 dark-gray scene with blue character sea + white foam
      const sketch = (p: any) => {
        // character pool: lowercase + UPPERCASE + digits
        const CHARSET =
          "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

        // palette -- contrast boosted between sea and foam
        const BG = { r: 240, g: 237, b: 230 };       // soft warm off-white (paper / cream)
        // sea base: vertical gradient from deeper at the top to very deep at bottom
        // (darker overall -> stronger contrast vs. white foam)
        const SEA_TOP = { r: 22, g: 68, b: 175 };    // darker, more saturated blue near surface
        const SEA_DEEP = { r: 3, g: 10, b: 50 };     // almost-black blue at the bottom
        // character color: bright but still readable on dark sea, much brighter than sea base
        const SEA = { r: 130, g: 190, b: 255 };      // bright sky-blue characters (high contrast)
        const FOAM = { r: 255, g: 255, b: 255 };     // pure white foam

        // per-character flicker phase grid -- independent timing for every cell
        let phaseGrid: number[][] = [];
        let speedGrid: number[][] = [];

        let cw = 0;
        let ch = 0;
        let cellW = 0;
        let cellH = 0;
        let cols = 0;
        let rows = 0;
        let charGrid: string[][] = [];
        type Dot = { x: number; y: number; vx: number; vy: number; r: number; a: number };
        let dots: Dot[] = [];

        // ===================== MOUSE TIDE INTERACTION =====================
        // Tide system: mouse Y position controls the global sea level.
        //   - mouse near top of canvas    -> HIGH TIDE  (sea rises, covers more screen)
        //   - mouse near bottom of canvas -> LOW TIDE   (sea recedes, reveals background)
        //   - mouse outside canvas        -> tide eases back to neutral level
        // `tideOffset` is added directly to the wave base Y inside waveTopY().
        // Negative value  -> waveTop sits higher on screen -> high tide.
        // Positive value  -> waveTop sits lower  on screen -> low tide.
        // Eased every frame so the rise/fall is slow & continuous, never jumpy.
        //
        // [REST POSITION] -- The wave's static / resting Y position is
        // ALWAYS ch * REST_RATIO, regardless of mouse interactions. When
        // the mouse is not active, tideOffset reliably eases back to 0,
        // so the wave always settles to this exact line.
        const REST_RATIO = 0.48; // wave midline at 48% of canvas height
        let tideOffset = 0;
        // [TIDE INTENSITY] 0 at neutral/low tide, 1 at maximum high tide.
        // Drives foam exaggeration (thicker / more violent crest) at peak.
        let tideIntensity = 0;
        let mx = -9999;
        let my = -9999;
        // ==================================================================

        // canvas fills the full viewport (full width & full height)
        const computeSize = () => {
          const parent = containerRef.current!;
          cw = Math.floor(parent.clientWidth);
          ch = Math.floor(parent.clientHeight);
        };

        // dense character grid sized to current canvas.
        // Cell size: denser than before -> richer particle/character texture
        // across the whole sea body, especially noticeable in the lower half.
        const buildGrid = () => {
          cellW = Math.max(9, Math.floor(cw / 150)); // denser cells -> richer texture
          cellH = Math.max(11, Math.floor(cellW * 1.2));
          cols = Math.ceil(cw / cellW) + 2;
          rows = Math.ceil(ch / cellH) + 2;
          charGrid = [];
          phaseGrid = [];
          speedGrid = [];
          for (let r = 0; r < rows; r++) {
            const row: string[] = [];
            const phaseRow: number[] = [];
            const speedRow: number[] = [];
            for (let c = 0; c < cols; c++) {
              row.push(CHARSET.charAt(Math.floor(Math.random() * CHARSET.length)));
              // unique phase offset per cell
              phaseRow.push(Math.random() * 1000);
              // slow random flicker speed (0.35 ~ 0.95) -- gentle, not strobing
              speedRow.push(0.35 + Math.random() * 0.6);
            }
            charGrid.push(row);
            phaseGrid.push(phaseRow);
            speedGrid.push(speedRow);
          }
        };

        // ===================== BEACH (sand + shells) =====================
        // Sand grain & shell layout is computed ONCE per resize. It stays put
        // (doesn't shimmer / move around), only gets covered up or revealed by
        // the rising / falling tide. Each sand grain has a fixed position,
        // size, tone (warm/cool sand variation) and a baked random alpha so
        // the beach looks like a real photo of sand, not animated noise.
        // Each grain carries its own color, alpha, shape (1=dot/2=streak), and
        // a tiny rotation so the beach reads as a real photographic texture
        // instead of a uniform stipple.
        type SandGrain = {
          x: number;
          y: number;
          w: number;
          h: number;
          rot: number;
          r: number;
          g: number;
          b: number;
          a: number;
        };
        let sandGrains: SandGrain[] = [];

        // BEACH_TOP defines how far down the page the beach actually starts.
        // Above this Y there's mostly empty sky/cream background; below it
        // sand grains and shells live, and may get submerged by the rising sea.
        const beachTop = () => 0; // beach occupies the WHOLE area above the sea

        const buildBeach = () => {
          sandGrains = [];

          // ---- sand grains (high-detail) ----
          // Much denser than before so the beach reads as a true photographic
          // sand texture: tons of tiny specks, a smaller layer of mid grains,
          // and rare bigger flecks (small pebbles / shell fragments).
          //
          // Three-pass generation produces a layered, non-uniform distribution.

          // Pass A: micro grit -- very fine pixel dust, super dense.
          const microCount = Math.floor((cw * ch) / 55);
          for (let i = 0; i < microCount; i++) {
            const gx = Math.random() * cw;
            const bias = Math.pow(Math.random(), 0.5);
            const gy = beachTop() + bias * (ch * 0.92);
            const toneRoll = Math.random();
            let r: number, g: number, b: number, a: number;
            if (toneRoll < 0.5) {
              // pale warm sand fleck
              r = 218 + Math.random() * 18;
              g = 210 + Math.random() * 16;
              b = 192 + Math.random() * 16;
              a = 55 + Math.random() * 55;
            } else if (toneRoll < 0.82) {
              // mid warm tan
              r = 188 + Math.random() * 20;
              g = 170 + Math.random() * 18;
              b = 142 + Math.random() * 18;
              a = 75 + Math.random() * 65;
            } else if (toneRoll < 0.95) {
              // cooler grey grain for tonal variety
              r = 165 + Math.random() * 18;
              g = 160 + Math.random() * 16;
              b = 150 + Math.random() * 16;
              a = 70 + Math.random() * 55;
            } else {
              // tiny dark fleck (organic / pebble)
              r = 70 + Math.random() * 30;
              g = 60 + Math.random() * 25;
              b = 50 + Math.random() * 22;
              a = 140 + Math.random() * 80;
            }
            sandGrains.push({
              x: gx, y: gy,
              w: 1, h: 1, rot: 0,
              r, g, b, a,
            });
          }

          // Pass B: small grains -- slightly chunkier, lower density.
          const smallCount = Math.floor((cw * ch) / 480);
          for (let i = 0; i < smallCount; i++) {
            const gx = Math.random() * cw;
            const bias = Math.pow(Math.random(), 0.55);
            const gy = beachTop() + bias * (ch * 0.9);

            // most are tiny dots; some are mini elongated streaks (sand grains
            // are not perfect circles) -- adds a real-photo grit feel
            const isStreak = Math.random() < 0.32;
            const w = isStreak ? 1.8 + Math.random() * 1.4 : 1.4 + Math.random() * 0.8;
            const h = isStreak ? 1 : w;
            const rot = isStreak ? Math.random() * Math.PI : 0;

            const toneRoll = Math.random();
            let r: number, g: number, b: number, a: number;
            if (toneRoll < 0.4) {
              r = 205 + Math.random() * 22;
              g = 192 + Math.random() * 20;
              b = 168 + Math.random() * 18;
              a = 110 + Math.random() * 70;
            } else if (toneRoll < 0.78) {
              r = 170 + Math.random() * 25;
              g = 152 + Math.random() * 22;
              b = 122 + Math.random() * 20;
              a = 130 + Math.random() * 70;
            } else if (toneRoll < 0.92) {
              // brownish grain
              r = 130 + Math.random() * 25;
              g = 110 + Math.random() * 22;
              b = 85 + Math.random() * 20;
              a = 140 + Math.random() * 70;
            } else {
              // dark fleck
              r = 65 + Math.random() * 30;
              g = 55 + Math.random() * 25;
              b = 42 + Math.random() * 22;
              a = 180 + Math.random() * 60;
            }
            sandGrains.push({ x: gx, y: gy, w, h, rot, r, g, b, a });
          }

          // Pass C: rare bigger flecks (pebble bits, shell shrapnel) -- adds
          // larger-scale visual variation so the eye finds detail at many
          // zoom levels.
          const bigCount = Math.floor((cw * ch) / 4500);
          for (let i = 0; i < bigCount; i++) {
            const gx = Math.random() * cw;
            const bias = Math.pow(Math.random(), 0.7);
            const gy = beachTop() + bias * (ch * 0.88);
            const w = 2.2 + Math.random() * 2.0;
            const h = 1.4 + Math.random() * 1.6;
            const rot = Math.random() * Math.PI;

            const toneRoll = Math.random();
            let r: number, g: number, b: number, a: number;
            if (toneRoll < 0.5) {
              // warm tan pebble
              r = 150 + Math.random() * 30;
              g = 130 + Math.random() * 28;
              b = 100 + Math.random() * 25;
              a = 170 + Math.random() * 60;
            } else if (toneRoll < 0.85) {
              // dark pebble
              r = 78 + Math.random() * 30;
              g = 65 + Math.random() * 25;
              b = 50 + Math.random() * 22;
              a = 190 + Math.random() * 55;
            } else {
              // bright pale fragment (shell bits)
              r = 230 + Math.random() * 20;
              g = 220 + Math.random() * 18;
              b = 200 + Math.random() * 16;
              a = 180 + Math.random() * 60;
            }
            sandGrains.push({ x: gx, y: gy, w, h, rot, r, g, b, a });
          }
        };
        // ================================================================

        // sparse drifting white dust particles
        const buildDots = () => {
          dots = [];
          const count = Math.floor((cw * ch) / 10000); // sparse
          for (let i = 0; i < count; i++) {
            dots.push({
              x: Math.random() * cw,
              y: Math.random() * ch,
              vx: (Math.random() - 0.5) * 0.12,
              vy: (Math.random() - 0.5) * 0.12,
              r: Math.random() * 1.2 + 0.3,
              a: Math.random() * 120 + 60,
            });
          }
        };

        p.setup = () => {
          computeSize();
          const canvas = p.createCanvas(cw, ch);
          canvas.parent(containerRef.current!);
          p.textFont("monospace");
          p.textAlign(p.CENTER, p.CENTER);
          buildGrid();
          buildDots();
          buildBeach();
        };

        p.windowResized = () => {
          computeSize();
          p.resizeCanvas(cw, ch);
          buildGrid();
          buildDots();
          buildBeach();
        };

        // ===================== MOUSE TIDE INTERACTION =====================
        // Per-frame tide update.
        //   target tide is computed from mouse Y position:
        //     mouseY near top    -> strongly negative (sea level lifts up)
        //     mouseY near bottom -> strongly positive (sea level drops down)
        //   When the mouse leaves the canvas, target -> 0 (neutral level).
        //   `tideOffset` eases toward the target every frame.
        const updateTide = () => {
          mx = p.mouseX;
          my = p.mouseY;
          const insideCanvas = mx >= 0 && mx <= cw && my >= 0 && my <= ch;

          let target = 0;
          if (insideCanvas) {
            // Normalized Y: 0 at top, 1 at bottom.
            const ny = my / ch;
            // Map [0,1] -> [-1, +1]:  top => -1 (rise), bottom => +1 (fall).
            const k = ny * 2 - 1;
            // Maximum tide displacement: up to ~32% of canvas height.
            // Rising (k<0) is given a bit more range so high tide feels dramatic.
            const maxRise = ch * 0.32;
            const maxFall = ch * 0.20;
            target = k < 0 ? k * maxRise : k * maxFall;
          }

          // Slow ease toward target -> rising/falling tide feels gradual.
          // When mouse is INSIDE the canvas, response is gentle (0.04) so
          // the tide rises smoothly under your cursor.
          // When mouse LEAVES the canvas, return is MUCH faster (0.18) so
          // the wave decisively snaps back toward the rest line instead of
          // drifting lazily. The further the wave is from rest, the more
          // urgent the pull -- we also add a small accelerating component
          // proportional to the current offset magnitude so big high-tide
          // states return especially quickly.
          let easeRate: number;
          if (insideCanvas) {
            easeRate = 0.04;
          } else {
            // Base fast return + extra acceleration proportional to how far
            // we are from rest (normalized by max possible rise). Caps at ~0.32.
            const maxRise = ch * 0.32;
            const distRatio = p.constrain(Math.abs(tideOffset) / maxRise, 0, 1);
            easeRate = 0.18 + distRatio * 0.14;
          }
          tideOffset += (target - tideOffset) * easeRate;

          // Snap to 0 once the residual offset is below 1 sub-pixel so
          // floating-point drift can never leave the wave hovering above
          // or below the true rest position over long sessions.
          if (!insideCanvas && Math.abs(tideOffset) < 0.3) {
            tideOffset = 0;
          }

          // tideIntensity: how close we are to MAX high tide (0..1).
          // Only rising (negative tideOffset) counts -- falling tide stays calm.
          const maxRise = ch * 0.32;
          tideIntensity = p.constrain(-tideOffset / maxRise, 0, 1);
        };
        // ==================================================================

        // wave-top Y for given x and time (drifts left->right).
        // -------------------------------------------------------------
        // Built from FIVE travelling sinusoids of different frequencies,
        // amplitudes, speeds and directions, plus two layers of perlin
        // noise (slow swell + fast chop). The mix produces a realistic
        // multi-scale ocean surface: long swells underneath, choppy
        // mid-frequency rollers, and tiny surface ripples on top.
        //
        // Finally we sharpen the crests with a soft asymmetric reshape so
        // that wave tops feel pointed and troughs feel rounded -- like
        // real water under wind, not pure cosine curves.
        // -------------------------------------------------------------
        const waveTopY = (x: number, t: number) => {
          const base = ch * REST_RATIO + tideOffset; // tide shifts base level

          // Long swell (low frequency, slow, big amplitude)
          const s1 = Math.sin(x * 0.0042 - t * 0.55 + 1.3) * (ch * 0.075);
          // Mid roller (the body of the wave)
          const s2 = Math.sin(x * 0.0090 - t * 0.90) * (ch * 0.052);
          // Counter-traveling wave for interference "beats"
          const s3 = Math.sin(x * 0.0125 + t * 0.45 + 0.7) * (ch * 0.030);
          // Mid-high chop
          const s4 = Math.sin(x * 0.0185 - t * 1.20 + 2.1) * (ch * 0.020);
          // Tiny surface ripples (very fast, very small)
          const s5 = Math.sin(x * 0.040 - t * 2.2 + 0.4) * (ch * 0.008);

          // Slow swell perlin (large-scale shape variation)
          const swell = (p.noise(x * 0.0017 - t * 0.06, t * 0.10) - 0.5) * (ch * 0.085);
          // Fast chop perlin (small surface texture)
          const chop = (p.noise(x * 0.012 + 91, t * 0.55) - 0.5) * (ch * 0.018);

          let y = base + s1 + s2 + s3 + s4 + s5 + swell + chop;

          // Crest sharpening: shift peaks slightly upward, troughs slightly
          // downward, mimicking Stokes-style real ocean waves (sharp crests,
          // rounded valleys). Strength is gentle so we don't break the foam.
          const dy = y - base;
          const k = 0.18; // sharpening strength
          // Negative dy = above base = crest -> make it MORE negative (taller)
          // Positive dy = below base = trough -> flatten slightly
          if (dy < 0) {
            y -= Math.abs(dy) * k;
          } else {
            y -= dy * k * 0.45;
          }
          return y;
        };

        p.draw = () => {
          const t = p.millis() / 1000;

          // 0) light gray background
          p.background(BG.r, BG.g, BG.b);

          // 0.5) BEACH layer: sand grains + scattered shells on the dry sand.
          //      Drawn before the sea so anything below the wave crest gets
          //      automatically buried by the water (and revealed at low tide).
          drawBeach(t);

          // [INTERACTION] update tide level from mouse position
          updateTide();

          // 1) deep-blue sea base that fills everything below the wave top
          //    (this guarantees no gap can ever appear at the bottom)
          drawSeaBase(t);

          // 2) granular dot-noise layer painted across the entire sea body.
          //    This is the dominant "grain" of the blue sea -- denser & clearer
          //    than characters alone, with light/dark micro-contrast against the
          //    deep blue base. Heavier toward the bottom half.
          drawSeaGrain(t);

          // 3) dense blue character sea drawn on top of the grain.
          //    Each character has its own slow flicker -> independent fade in/out.
          //    Perf: find the row range that overlaps the sea each frame so we
          //    skip entire rows above the wave crest.
          p.textSize(cellH * 1.1); // slightly thicker glyphs vs. cell size
          p.textStyle(p.BOLD);
          p.noStroke();
          // approx min wave-top so we can skip rows fully above the sea
          // (account for tideOffset so high tide doesn't get clipped)
          const approxMinSurface =
            ch * 0.48 + tideOffset - ch * 0.05 - ch * 0.07 - ch * 0.02 - ch * 0.05;
          const startRow = Math.max(0, Math.floor(approxMinSurface / cellH) - 2);
          for (let r = startRow; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
              const baseX = c * cellW + cellW / 2;
              const baseY = r * cellH + cellH / 2;

              // small perlin offset -> organic deformation, not noisy
              const nx = p.noise(c * 0.16, r * 0.2, t * 0.3);
              const ny = p.noise(c * 0.2 + 50, r * 0.16 + 50, t * 0.3);
              const offX = (nx - 0.5) * cellW * 1.1;
              const offY = (ny - 0.5) * cellH * 1.3;

              let x = baseX + offX;
              let y = baseY + offY;

              // ============ WAVE ROLLING (sea characters tumble with foam) ============
              // Real water particles in a surface wave travel on circular orbits.
              // We mimic that: each character gets a circular offset whose phase is
              // synced to the dominant wave (same x*0.009 - t*0.9 as waveTopY) so
              // characters near the surface roll forward & back in lock-step with
              // the foam crest. Orbit radius decays with depth -> deep water sits
              // still while surface water tumbles. Result: the whole sea visibly
              // "rolls" along with the wave instead of sitting frozen.
              const surfaceY0 = waveTopY(baseX, t);
              const depthRatio = p.constrain((baseY - surfaceY0) / (ch - surfaceY0 + 1), 0, 1);
              // surface-weighted decay (sharp falloff into the deep)
              const rollWeight = Math.pow(1 - depthRatio, 1.6);
              const rollPhase = baseX * 0.009 - t * 0.9;
              const rollAmpX = cellW * 1.4 * rollWeight;
              const rollAmpY = cellH * 0.9 * rollWeight;
              x += Math.cos(rollPhase) * rollAmpX;
              y += Math.sin(rollPhase) * rollAmpY;
              // ========================================================================

              const surfaceY = waveTopY(x, t);
              if (y < surfaceY) continue; // skip above water

              // depth: 0 at surface, 1 at bottom
              const depth = p.constrain((y - surfaceY) / (ch - surfaceY), 0, 1);
              // Mid-depth is the brightest zone, then characters TAPER OFF as
              // they sink toward the seabed -- giving the deep water a soft
              // "vanishing into the dark" feel instead of a hard bottom line.
              //   depth 0.00 -> 0.55 : ramp up from 140 to 255 (surface -> mid)
              //   depth 0.55 -> 1.00 : fade from 255 down to 0 (mid -> seabed)
              let baseAlpha: number;
              if (depth < 0.55) {
                baseAlpha = p.lerp(140, 255, depth / 0.55);
              } else {
                // smooth cubic-ish falloff so the fade looks organic, not linear
                const k = (depth - 0.55) / 0.45; // 0..1 across the fade zone
                const fade = 1 - Math.pow(k, 1.4);
                baseAlpha = 255 * fade;
              }

              // ============ IRREGULAR BOTTOM DISSOLVE ============
              // Instead of a uniform horizontal fade band, the dissolve
              // boundary varies PER COLUMN (and slowly drifts over time)
              // using multi-frequency perlin noise. This produces
              // stalactite / rain-trail shapes where some columns reach
              // deep into the dark while neighboring columns vanish much
              // higher up -- the organic "dripping characters" look from
              // the reference.
              //
              // We compute a per-(x, slow-time) dissolve threshold: the Y
              // above which alpha is full, below which it tapers to 0 by
              // a randomized distance. Both threshold and taper distance
              // are driven by independent noise channels.
              const nCol = p.noise(baseX * 0.0055, t * 0.04);          // big shape
              const nColFine = p.noise(baseX * 0.022 + 80, t * 0.07);  // small jitter
              // dissolveStart: column-specific Y where the fade begins.
              //   Range roughly ch * 0.55 (high columns) .. ch * 0.95 (deep columns).
              const dissolveStart =
                ch * (0.55 + nCol * 0.32 + (nColFine - 0.5) * 0.08);
              // tail length: how gradually this column fades to nothing.
              //   Some columns have long soft trails; others snap off short.
              const tailLen = ch * (0.06 + nCol * 0.22);

              if (y > dissolveStart) {
                const k = (y - dissolveStart) / tailLen;
                if (k >= 1) continue; // beyond this column's reach -> gone
                // Per-character random gating: deeper into the tail, more
                // characters drop out entirely, creating speckled holes
                // rather than a smooth gradient (matches reference image 2).
                const dropChance = Math.pow(k, 0.7);
                // Stable per-cell random based on phase grid value
                const cellRand = (Math.sin(phaseGrid[r][c] * 12.9898) * 43758.5453) % 1;
                const stableRand = cellRand < 0 ? cellRand + 1 : cellRand;
                if (stableRand < dropChance) continue;
                // Remaining characters fade by a soft curve.
                baseAlpha *= Math.pow(1 - k, 1.3);
              }
              // =====================================================

              // ---- per-character slow flicker (fade out + reappear) ----
              const phase = phaseGrid[r][c];
              const speed = speedGrid[r][c];
              const wave = Math.sin(t * speed + phase); // [-1, 1]
              const flicker = p.constrain(0.55 + wave * 0.6, 0, 1);
              // cheap pseudo-blink using sin instead of noise (much faster)
              const blinkPulse = Math.sin(t * 0.4 + phase * 1.7);
              const blink = blinkPulse < -0.78 ? 0.0 : 1.0;
              const finalAlpha = baseAlpha * flicker * blink;
              if (finalAlpha < 4) continue;

              p.fill(SEA.r, SEA.g, SEA.b, finalAlpha);
              p.text(charGrid[r][c], x, y);
            }
          }
          p.textStyle(p.NORMAL);

          // 4) thick fluffy white foam band along the wave crest
          //    (waveTopY already includes tideOffset, so foam rises/falls too)
          drawFoam(t);

          // 5) sparse white dust across the canvas
          drawDots();
        };

        // Draws the beach: dense, multi-scale sand grain texture.
        // Grains live above the current wave top; as the tide rises, grains
        // get submerged and vanish smoothly along the wet-line; at low tide
        // more beach is revealed naturally.
        const drawBeach = (t: number) => {
          // wet-line offset: a few px ABOVE the actual wave top so sand right
          // at the shoreline appears darker / damp, blending into the foam.
          const wetFadeDist = ch * 0.045;
          const dampShadowDist = ch * 0.07;

          p.noStroke();
          for (const sg of sandGrains) {
            const surfaceY = waveTopY(sg.x, t);
            if (sg.y >= surfaceY) continue; // submerged
            // closer to the wave -> dampen alpha so the wet line reads smoothly
            const distFromWave = surfaceY - sg.y;
            const wetFactor = p.constrain(distFromWave / wetFadeDist, 0, 1);
            const alpha = sg.a * wetFactor;
            if (alpha < 3) continue;

            // bigger grains carry a faint shadow on the damp side so the
            // beach has subtle 3D micro-relief instead of looking flat.
            const isBig = sg.w >= 2;
            if (isBig) {
              const dampShadow = p.constrain(1 - distFromWave / dampShadowDist, 0, 1) * 60;
              if (dampShadow > 5) {
                p.fill(95, 80, 60, dampShadow);
                if (sg.rot !== 0) {
                  p.push();
                  p.translate(sg.x + 0.5, sg.y + 0.4);
                  p.rotate(sg.rot);
                  p.rect(0, 0, sg.w, sg.h);
                  p.pop();
                } else {
                  p.rect(sg.x + 0.5, sg.y + 0.4, sg.w, sg.h);
                }
              }
            }

            p.fill(sg.r, sg.g, sg.b, alpha);
            if (sg.rot !== 0) {
              p.push();
              p.translate(sg.x, sg.y);
              p.rotate(sg.rot);
              p.rect(0, 0, sg.w, sg.h);
              p.pop();
            } else {
              p.rect(sg.x, sg.y, sg.w, sg.h);
            }
          }
        };

        // Sea base: a solid water body that hugs the wave top and extends past
        // the bottom of the canvas. Rendered as a vertical gradient (lighter at
        // the surface, darker at the bottom) using stacked layered polygons.
        // This also acts as the "bottom fallback" so the sea never has a gap.
        const drawSeaBase = (t: number) => {
          const step = Math.max(3, Math.floor(cw / 280));

          // Pre-sample wave-top points once and reuse for every layer.
          const points: { x: number; y: number }[] = [];
          for (let x = -step * 4; x <= cw + step * 4; x += step) {
            points.push({ x, y: waveTopY(x, t) });
          }

          // [TIDE FIX] Find the highest (smallest-y) wave point this frame so
          // the gradient thresholds anchor to the ACTUAL current sea surface,
          // not a fixed ch fraction. Without this, raising the tide above the
          // old fixed threshold (~ch*0.3) caused the top layer to detach from
          // the wave crest and reveal background between wave and sea base.
          let minWaveY = Infinity;
          for (const pt of points) {
            if (pt.y < minWaveY) minWaveY = pt.y;
          }

          // Number of gradient layers -- more layers = smoother gradient.
          const LAYERS = 14;
          p.noStroke();
          for (let i = 0; i < LAYERS; i++) {
            const ratio = i / (LAYERS - 1); // 0 = surface, 1 = bottom
            // Color interpolated from SEA_TOP -> SEA_DEEP
            const r = p.lerp(SEA_TOP.r, SEA_DEEP.r, ratio);
            const g = p.lerp(SEA_TOP.g, SEA_DEEP.g, ratio);
            const b = p.lerp(SEA_TOP.b, SEA_DEEP.b, ratio);

            // Threshold now follows the live wave: layer 0 hugs the wave crest
            // exactly (no clamp), darker layers progressively sit further down
            // toward the bottom. This guarantees the sea base ALWAYS reaches
            // the wave top at high tide, no matter how far up the sea rises.
            const threshold = minWaveY + (ch - minWaveY) * ratio;

            p.fill(r, g, b, 255);
            p.beginShape();
            for (const pt of points) {
              p.vertex(pt.x, Math.max(pt.y, threshold));
            }
            p.vertex(cw + step * 4, ch + 40);
            p.vertex(-step * 4, ch + 40);
            p.endShape(p.CLOSE);
          }
        };

        // White foam along the wave crest.
        // Built from MANY translucent layers stacked together, so the foam reads
        // as a soft fluffy body instead of one hard white line:
        //   - wider, more translucent layers underneath (haze, blending with sea)
        //   - narrower, more opaque layers on top (the brighter foam core)
        //   - irregular bumpy edges driven by multi-frequency noise
        //   - scattered tiny foam dots for fine fluffy detail (NOT a hard outline)
        const drawFoam = (t: number) => {
          const step = Math.max(3, Math.floor(cw / 280));
          const points: { x: number; y: number }[] = [];
          for (let x = -step * 4; x <= cw + step * 4; x += step) {
            points.push({ x, y: waveTopY(x, t) });
          }

          p.noStroke();

          // ============ HIGH-TIDE FOAM AMPLIFICATION ============
          // At max high tide (tideIntensity = 1) foam becomes substantially
          // thicker, more violent and slightly brighter to sell the climax of
          // the tide. At rest these multipliers all collapse to 1.0.
          const thickBoost = 1 + tideIntensity * 1.1;  // up to ~2.1x thicker
          const bumpBoost  = 1 + tideIntensity * 1.6;  // up to ~2.6x bumpier
          const alphaBoost = 1 + tideIntensity * 0.35; // slightly brighter peak
          // ======================================================

          // -------- BLUE SHADOW BAND under the foam --------
          // A SMOOTH multi-stop blue gradient sitting just under the foam.
          // The previous 4-layer approach had visible color-step seams
          // because adjacent translucent polygons created tonal banding.
          // Now we paint ~24 thin sub-bands across a single gradient range,
          // each one interpolating both COLOR and ALPHA from a "churned
          // water" tone right at the crest down to fully-transparent at
          // the bottom of the band. This produces a continuous, seamless
          // tonal modulation rather than discrete stripes.
          //
          // All colors stay locked to the existing sea palette so the
          // shadow reads as the SAME water, just slightly modulated.
          const shadowTotal = ch * 0.075;            // total vertical reach
          const SUB_BANDS = 24;                       // smoothness (more = smoother)
          const subThick = shadowTotal / SUB_BANDS * 1.6; // overlap a bit for blending

          // Gradient stops: top (churned water under foam) -> bottom (deep sea)
          const top = { r: 55, g: 105, b: 200, a: 95 };
          const mid = { r: SEA_TOP.r, g: SEA_TOP.g, b: SEA_TOP.b, a: 55 };
          const bot = { r: SEA_DEEP.r, g: SEA_DEEP.g, b: SEA_DEEP.b, a: 0 };

          // Helper to interpolate the gradient at 0..1
          const sampleShadow = (u: number) => {
            if (u < 0.5) {
              const k = u / 0.5;
              return {
                r: p.lerp(top.r, mid.r, k),
                g: p.lerp(top.g, mid.g, k),
                b: p.lerp(top.b, mid.b, k),
                a: p.lerp(top.a, mid.a, k),
              };
            }
            const k = (u - 0.5) / 0.5;
            return {
              r: p.lerp(mid.r, bot.r, k),
              g: p.lerp(mid.g, bot.g, k),
              b: p.lerp(mid.b, bot.b, k),
              a: p.lerp(mid.a, bot.a, k),
            };
          };

          for (let s = 0; s < SUB_BANDS; s++) {
            const u = s / (SUB_BANDS - 1); // 0..1 across the whole shadow band
            const c = sampleShadow(u);
            if (c.a < 1) continue;
            const yOffsetTop = u * shadowTotal;
            p.fill(c.r, c.g, c.b, c.a);
            p.beginShape();
            // top edge of this sub-band
            for (let i = 0; i < points.length; i++) {
              const pt = points[i];
              p.vertex(pt.x, pt.y + yOffsetTop);
            }
            // bottom edge: same shape, shifted down by `subThick`
            // Light noise breaks up any horizontal regularity.
            for (let i = points.length - 1; i >= 0; i--) {
              const pt = points[i];
              const bump =
                (p.noise(pt.x * 0.012 - t * 0.1, t * 0.3 + s * 0.6 + i * 0.04) - 0.5) *
                subThick * 0.5;
              p.vertex(pt.x, pt.y + yOffsetTop + subThick + bump);
            }
            p.endShape(p.CLOSE);
          }

          // -------- WHITE FOAM LAYERS --------
          // Foam layers definition.
          // thicknessTop  -> how far the layer extends ABOVE the wave crest
          // thicknessBot  -> how far the layer dips BELOW the crest into the sea
          // alpha         -> opacity (wider layers are more translucent)
          // freq / amp    -> noise frequency & bump amplitude for irregular edges
          // yShift        -> vertical bias (>0 means layer sits lower, <0 higher)
          //
          // NOTE: thicknessBot is now much smaller across the board -- the
          // "below the crest" zone is owned by the blue shadow band above,
          // not by white haze. This stops the foam looking like it's bleeding
          // a cream/off-white wash into the sea.
          //
          // The brightest cores are boosted further so the very middle of
          // the foam reads as pure, snow-bright white.
          const layers = [
            // 1) very wide soft haze that blends foam upward into the air
            { thicknessTop: ch * 0.075, thicknessBot: ch * 0.004, alpha: 22,  freq: 0.010, amp: 1.8, yShift: ch * 0.002 },
            // 2) wide soft foam haze
            { thicknessTop: ch * 0.058, thicknessBot: ch * 0.003, alpha: 45,  freq: 0.013, amp: 1.7, yShift: ch * 0.001 },
            // 3) medium translucent foam
            { thicknessTop: ch * 0.045, thicknessBot: ch * 0.003, alpha: 95,  freq: 0.018, amp: 1.6, yShift: 0 },
            // 4) bright foam middle layer
            { thicknessTop: ch * 0.034, thicknessBot: ch * 0.002, alpha: 160, freq: 0.022, amp: 1.5, yShift: -ch * 0.002 },
            // 5) thinner brighter foam
            { thicknessTop: ch * 0.024, thicknessBot: ch * 0.002, alpha: 215, freq: 0.030, amp: 1.4, yShift: -ch * 0.004 },
            // 6) bright foam crest -- now opaque
            { thicknessTop: ch * 0.016, thicknessBot: ch * 0.001, alpha: 250, freq: 0.040, amp: 1.3, yShift: -ch * 0.006 },
            // 7) brightest narrow foam core -- PURE WHITE
            { thicknessTop: ch * 0.009, thicknessBot: ch * 0.001, alpha: 255, freq: 0.055, amp: 1.2, yShift: -ch * 0.008 },
            // 8) inner hyper-bright core: solid white on the crest
            { thicknessTop: ch * 0.005, thicknessBot: ch * 0.0, alpha: 255, freq: 0.075, amp: 1.0, yShift: -ch * 0.010 },
            // 9) NEW: razor-thin SNOW WHITE highlight -- the absolute brightest
            //    midline of the foam. Gives the "sun-hit" peak its perfect white.
            { thicknessTop: ch * 0.0028, thicknessBot: ch * 0.0, alpha: 255, freq: 0.095, amp: 0.7, yShift: -ch * 0.0115 },
          ];

          // Render each layer as a bumpy polygon hugging the wave crest.
          for (let li = 0; li < layers.length; li++) {
            const L = layers[li];
            // [TIDE] amplify each layer by the high-tide multipliers above.
            const layerThickTop = L.thicknessTop * thickBoost;
            const layerThickBot = L.thicknessBot * thickBoost;
            const layerAmp = L.amp * bumpBoost;
            const layerAlpha = Math.min(255, L.alpha * alphaBoost);
            p.fill(FOAM.r, FOAM.g, FOAM.b, layerAlpha);
            p.beginShape();
            // Top edge of this foam layer.
            // Two noise frequencies stacked -> big rolling bumps + small fluff.
            for (let i = 0; i < points.length; i++) {
              const pt = points[i];
              const bumpBig =
                (p.noise(pt.x * L.freq - t * 0.12, t * 0.35 + li * 1.7 + i * 0.04) - 0.5) *
                layerThickTop * layerAmp;
              const bumpSmall =
                (p.noise(pt.x * (L.freq * 3.5) - t * 0.2, t * 0.6 + li * 2.1 + i * 0.08) - 0.5) *
                layerThickTop * 0.6;
              p.vertex(pt.x, pt.y + L.yShift - layerThickTop + bumpBig + bumpSmall);
            }
            // Bottom edge -- gently dips into the sea so foam blends, not cuts.
            for (let i = points.length - 1; i >= 0; i--) {
              const pt = points[i];
              const bump =
                (p.noise(pt.x * (L.freq * 1.3) + 700 - t * 0.1, t * 0.4 + li * 1.3 + i * 0.05) - 0.5) *
                layerThickBot * 1.4;
              p.vertex(pt.x, pt.y + L.yShift + layerThickBot + bump);
            }
            p.endShape(p.CLOSE);
          }

          // NOTE: extra white dots on top of the foam were intentionally removed
          // so the foam stays soft, fluffy and naturally graduated. The foam's
          // body now relies entirely on the stacked translucent layers above.
        };

        // Granular noise layer that gives the entire blue sea a clearly visible
        // particle texture. Optimized for performance:
        //   - larger step, fewer iterations
        //   - rect() instead of circle() (much faster)
        //   - skip aggressively when above-water
        const drawSeaGrain = (t: number) => {
          // bigger step than before -> fewer dots, big perf win, still dense feel
          const gStep = Math.max(3, Math.floor(cellW * 0.85));
          p.noStroke();
          // Animate the noise slowly via a single time offset, so the texture
          // shimmers gently without redoing 3D noise per cell.
          const tOff = t * 0.08;
          for (let y = 0; y < ch; y += gStep) {
            for (let x = 0; x < cw; x += gStep) {
              const surfaceY = waveTopY(x, t);
              if (y < surfaceY - 1) continue; // above water -> skip

              const depth = p.constrain((y - surfaceY) / (ch - surfaceY + 1), 0, 1);
              const densityThreshold = p.lerp(0.62, 0.34, depth);

              const n1 = p.noise(x * 0.08 + tOff, y * 0.08);
              if (n1 < densityThreshold) continue;

              const n2 = p.noise(x * 0.13 + 300, y * 0.13 + 300);

              const dotR = gStep * (0.4 + depth * 0.15);

              if (n2 > 0.55) {
                const alpha = 45 + (n2 - 0.55) * 240;
                p.fill(170, 210, 255, alpha);
              } else {
                const alpha = 60 + (0.55 - n2) * 260;
                p.fill(2, 8, 32, alpha);
              }
              // rect is much cheaper than circle and looks identical at this size
              p.rect(x, y, dotR, dotR);
            }
          }
        };

        const drawDots = () => {
          p.noStroke();
          for (const d of dots) {
            d.x += d.vx;
            d.y += d.vy;
            if (d.x < 0) d.x = cw;
            if (d.x > cw) d.x = 0;
            if (d.y < 0) d.y = ch;
            if (d.y > ch) d.y = 0;
            p.fill(FOAM.r, FOAM.g, FOAM.b, d.a);
            p.circle(d.x, d.y, d.r * 2);
          }
        };
      };

      p5Instance = new window.p5(sketch);
      window.__seaSketch = p5Instance;
    });

    return () => {
      if (p5Instance && typeof p5Instance.remove === "function") {
        p5Instance.remove();
      }
    };
  }, []);

  return (
    <div
      className="relative w-screen h-screen overflow-hidden m-0 p-0"
      style={{ background: "rgb(240,237,230)" }}
    >
      {/* p5 canvas as the base layer */}
      <div ref={containerRef} className="absolute inset-0 w-full h-full" />

      {/* Typography overlay -- editorial serif headline in the style of Aethera */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center">
        <div className="w-full max-w-[1180px] px-8 pt-[22vh]">
          <h1
            className="text-center leading-[1.02] tracking-[-0.025em]"
            style={{
              fontFamily: "'Fraunces', 'Times New Roman', serif",
              fontSize: "clamp(44px, 6.2vw, 88px)",
              fontOpticalSizing: "auto",
            }}
          >
            <span style={{ color: "rgb(12,28,78)", fontWeight: 500 }}>Beneath</span>
            <span
              style={{
                color: "rgb(120,140,180)",
                fontWeight: 300,
                fontStyle: "italic",
                fontVariationSettings: "'opsz' 144",
              }}
            >
              {" "}tide,
            </span>
            <span style={{ color: "rgb(12,28,78)", fontWeight: 500 }}> we listen</span>
            <span style={{ color: "rgb(120,140,180)", fontWeight: 300 }}> to the deep.</span>
          </h1>

          <p
            className="mx-auto mt-7 max-w-[560px] text-center"
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "15px",
              lineHeight: 1.65,
              color: "rgb(70,90,130)",
              fontWeight: 400,
            }}
          >
            A study of motion, character, and salt — where typography meets
            the rhythm of the sea and every wave writes a quiet sentence.
          </p>
        </div>
      </div>

      {/* Minimal top nav, matching the editorial reference */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20">
        <div className="mx-auto flex w-full max-w-[1280px] items-center justify-between px-8 py-6">
          <div
            className="pointer-events-auto cursor-pointer select-none"
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: "22px",
              fontWeight: 500,
              fontStyle: "italic",
              color: "rgb(12,28,78)",
              letterSpacing: "-0.01em",
            }}
          >
            Tidewriter<span style={{ fontSize: "12px", verticalAlign: "super" }}>®</span>
          </div>

          <nav
            className="pointer-events-auto hidden items-center gap-9 md:flex"
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "14px",
              fontWeight: 400,
            }}
          >
            <a className="cursor-pointer whitespace-nowrap text-[rgb(12,28,78)] hover:opacity-60" href="#">Home</a>
            <a className="cursor-pointer whitespace-nowrap text-[rgb(90,110,150)] hover:text-[rgb(12,28,78)]" href="#">Studio</a>
            <a className="cursor-pointer whitespace-nowrap text-[rgb(90,110,150)] hover:text-[rgb(12,28,78)]" href="#">Journal</a>
            <a className="cursor-pointer whitespace-nowrap text-[rgb(90,110,150)] hover:text-[rgb(12,28,78)]" href="#">Reach Us</a>
          </nav>

          <button
            type="button"
            className="pointer-events-auto cursor-pointer whitespace-nowrap rounded-full px-5 py-2.5 text-white transition-opacity hover:opacity-85"
            style={{
              background: "rgb(12,28,78)",
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              letterSpacing: "0.01em",
            }}
          >
            Begin Journey
          </button>
        </div>
      </div>
    </div>
  );
}